import type {NextPage} from 'next';
import Head from 'next/head';

import Footer from '@/components/common/footer/Footer';
import {Instruments} from '@/components/common/instruments-v2/Instruments';
import {TemplateForHiddenHeader} from '@/components/common/template-for-hidden-header/TemplateForHiddenHeader';
import appleTouchIcon from '@/components/page/survey/assets/apple-touch-icon.png';
import favicon from '@/components/page/survey/assets/favicon.ico';
import favicon16 from '@/components/page/survey/assets/favicon-16x16.png';
import favicon32 from '@/components/page/survey/assets/favicon-32x32.png';
import ogImage from '@/components/page/survey/assets/survey-og.webp';
import {DeviceAccess} from '@/components/page/survey/device-access/DeviceAccess';
import {FaqSection} from '@/components/page/survey/faq-section/FaqSection';
import {Promo} from '@/components/page/survey/promo/Promo';
import {SurveyFeats} from '@/components/page/survey/survey-feats/SurveyFeats';
import {SurveyUsers} from '@/components/page/survey/survey-users/SurveyUsers';
import {TariffsCalc} from '@/components/page/survey/tariffs-calc/TariffsCalc';
import {withThemeVariables} from '@/hocs/with-theme-variables';
import {useCheckOpenedModal} from '@/hooks/use-check-opened-modal';
import {useInjection} from '@/hooks/use-injection';
import {useOnceWhenEffect} from '@/hooks/use-once-when-effect';
import {useStartFeatures} from '@/hooks/use-start-features';
import variablesStyles from '@/pages/survey/survey-variables.module.css';
import {IFeatureService} from '@/services/feature/IFeatureService';
import {getCanonicalUrl} from '@/utilites/get-canonical-url';
import {insertJivoScript} from '@/utilites/script/insert-jivo-script';

import styles from './survey.module.css';

interface SurveyProps {}

const title =
	'Сервис для проведения опросов и анкетирования VK Workspace, автоматизация и сбор обратной связи от сотрудников в коллективе, управление удовлетворенностью сотрудников';
const ogTitle = title;
const description =
	'Проводите корпоративные опросы и повышайте эффективность внутренних процессов компании. Анонимные и публичные опросы, разные типы опросов, настройка целевой аудитории и просмотр результатов на любых устройствах.';

const Survey: NextPage<SurveyProps> = () => {
	const canonicalUrl = getCanonicalUrl('survey');

	const [featureService] = useInjection(IFeatureService);

	useStartFeatures();
	useCheckOpenedModal();
	useOnceWhenEffect(() => featureService.isServiceStarted, insertJivoScript);
	return (
		<div className={styles.root}>
			<Head>
				<title>{title}</title>
				<meta name="description" content={description} />

				<meta property="og:url" content={canonicalUrl} />
				<meta property="og:title" content={ogTitle} />
				<meta property="og:description" content={description} />
				<meta property="og:image" content={ogImage.src} />
				<link rel="canonical" href={canonicalUrl} />
				<link rel="icon" href={favicon.src} />
				<link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon.src} />
				<link rel="icon" type="image/png" sizes="32x32" href={favicon32.src} />
				<link rel="icon" type="image/png" sizes="16x16" href={favicon16.src} />
				<meta name="msapplication-TileColor" content="#da532c" />
				<meta name="theme-color" content="#000000" />
				<meta name="yandex-verification" content="786cac9e5ffdb048" />
				<meta name="google-site-verification" content="2VSZqo0aCUpXxV19XWK_55iC1O4eLPNlfpAhHvWZHow" />
			</Head>
			<TemplateForHiddenHeader contentStyle={styles.content}>
				<Promo />
				<SurveyFeats />
				<DeviceAccess />
				<SurveyUsers />
				<Instruments
					title="Опросы — один из сервисов VK WorkSpace. Используйте все инструменты платформы для совместной работы команд"
					className={styles.section}
				/>
				<TariffsCalc />
				<FaqSection />
				<Footer />
			</TemplateForHiddenHeader>
		</div>
	);
};

export default withThemeVariables(Survey, variablesStyles.cssLocalVariables);
